import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import { Avatar, Button, Col, Image, Row, Radio, Card, PaperIcon } from '@nackle/paper';
import { LocaleString, getString } from '@nackle/intl-tools';
import BudgetMeter from 'app/components/shared/budget-meter/budget-meter';
import { profileUrl, ecardUrl } from 'endpoints';
import { useDispatch, useSelector } from 'react-redux';
import { USER_STORAGE_KEY } from '../../../../constants';
import { getMessage, getSelectedEcard, setVisibility, getVisibility } from 'app/app-dux/message-dux';
import {
    getTotalAward,
    getRemainingBudget,
    getRemainingBudgetPercent,
    getSelectedBehavior,
    getSelectedProgram, getOriginalBudget
} from 'app/app-dux/program-dux';
import { getShortName, getAvatarLogoColor } from 'utils/color';
import { setActiveStep } from 'app/app-dux/app-dux';
import { getSelectedPeople } from 'app/app-dux/people-dux';
import { Unlock, Lock } from '../../shared/icons/icons';

import './review.less';

export function Review( props ) {
    let user = localStorage.getItem( USER_STORAGE_KEY );
    user = user ? JSON.parse( user ) : {};

    const [ animate, setAnimate ] = useState( false );
    const dispatch = useDispatch();

    const message = useSelector( getMessage );
    const remainingBudget = useSelector( getRemainingBudget );
    const remainingBudgetPercent = useSelector( getRemainingBudgetPercent );
    const selectedBehavior = useSelector( getSelectedBehavior );
    const selectedEcard = useSelector( getSelectedEcard );
    const selectedPeople = useSelector( getSelectedPeople );
    const selectedProgram = useSelector( getSelectedProgram );
    const totalAward = useSelector( getTotalAward );
    const originalBudget = useSelector( getOriginalBudget );
    const visibility = useSelector( getVisibility );

    const editPage = ( page ) => {
        dispatch( setActiveStep( page ) );
        props.changeRoute( page, false );
    };

    useEffect( () => {
        setAnimate( ( true ) );
    }, [] );

    const handleVisibility = ( visibility ) => {
        dispatch( setVisibility( visibility ) );
    };

    const programVisibility = selectedProgram && selectedProgram.visibility || [];
    const publicDisabled = programVisibility.length > 0 && !programVisibility.includes( 'PUBLIC' );
    const privateDisabled = programVisibility.length > 0 && !programVisibility.includes( 'PRIVATE' );

    const publicDisabledClasses = classNames( {
        'privacy-toggle': true,
        'disabled': publicDisabled
    } );
    const privateDisabledClasses = classNames( {
        'privacy-toggle': true,
        'disabled': privateDisabled
    } );

    return (
        <CSSTransition
            timeout={ 1000 }
            in={ animate }
            classNames={ props.forward ? 'slide-in' : 'slide-out' }
        >
            <div className='review-container spacer-lr-16'>
                <LocaleString
                    code='recognition.review.header'
                    TextComponent="h3"
                    className="header-text-bold"
                />
                <Card className='section-container'>
                    <div className='section-header'>
                        <div>
                            { selectedPeople.length === 1
                                ? <LocaleString code='recognition.people.totalRecipient' />
                                : <LocaleString code='recognition.people.totalRecipients' replaceOptions={ { recipientCount: selectedPeople.length } } />
                            }
                        </div>
                        <div className='edit-section'>
                            <Button onClick={ () => editPage( 'people' ) }>
                                <LocaleString code='recognition.button.edit' />
                            </Button>
                        </div>
                    </div>
                    <div className='recipient-list'>
                        { selectedPeople.map( ( recipient ) => {
                            return <div key={ `recipient-award-${ recipient.id }` } className='recipient-point-row'>
                                <div>
                                    <Avatar
                                        style={ getAvatarLogoColor( recipient ) }
                                        src={ `${ profileUrl }/public/images/image/${ user.claims?.company_id }/${ recipient.id }/avatar` }
                                    >
                                        { getShortName( recipient ) }
                                    </Avatar>
                                </div>
                                <div className='recipient-info'>
                                    <div className='team-recipient-name'>
                                        { `${ recipient.givenName } ${ recipient.surname }` }
                                    </div>
                                    { recipient?.personEmails?.length > 0 &&
                                    <div className="recipient-email">
                                        { recipient.personEmails[ 0 ].address }
                                    </div>
                                    }
                                    { ( recipient.department || recipient.jobTitle ) &&
                                    <div>
                                        { `${ recipient.department ? recipient.department : '' } ${ recipient.jobTitle ? recipient.jobTitle : '' }` }
                                    </div>
                                    }
                                </div>
                                { selectedProgram.hasPoints &&
                                <div className='point-entry'>
                                    { `${ recipient.awardValue } ${ getString( 'mediaLabel' ) }` }
                                </div>
                                }
                            </div>;
                        } ) }
                    </div>
                </Card>

                { ( selectedProgram.hasPoints || selectedBehavior ) &&
                <>
                    <Card className='section-container'>
                        <div className='section-header'>
                            <div>
                                <LocaleString code='recognition.review.awardHeader' />
                            </div>
                            <div className='edit-section'>
                                <Button onClick={ () => editPage( 'program' ) }>
                                    <LocaleString code='recognition.button.edit' />
                                </Button>
                            </div>
                        </div>
                        { selectedProgram.hasPoints &&
                        <div className='budget-slider'>
                            <BudgetMeter
                                percent={ remainingBudgetPercent }
                                current={ remainingBudget }
                                totalAward={ totalAward }
                                originalBudget={ originalBudget }
                                additionalInfo={ selectedProgram.rewardChoiceName }
                            />
                        </div>
                        }
                        { selectedBehavior &&
                        <div className='behavior-section'>
                            { selectedBehavior.imageUrl &&
                            <Avatar src={ selectedBehavior.imageUrl }></Avatar>
                            }
                            <div className='behavior-name'>{ selectedBehavior.name }</div>
                        </div>
                        }
                    </Card>
                </>
                }
                <Card className='section-container'>
                    <div className='section-header'>
                        <div>
                            <LocaleString code='recognition.review.personalizationHeader' />
                        </div>
                        <div className='edit-section'>
                            <Button onClick={ () => editPage( 'message' ) }>
                                <LocaleString code='recognition.button.edit' />
                            </Button>
                        </div>
                    </div>
                    <div className='personalization-container'>
                        <Row style={ { flex: 'auto' } }>
                            <Col className='message-privacy-container' span={ 16 }>
                                <div className='message-section'>
                                    { message }
                                </div>
                            </Col>
                            { selectedEcard && selectedEcard.images &&
                            <Col span={ 8 }>
                                <Image src={ `${ ecardUrl }/companies/${ user.claims.company_id }/ecards/${ selectedEcard.id }/locales/${ user.claims.locale }` }
                                       preview={ false }  />
                            </Col>
                            }
                        </Row>
                    </div>
                </Card>
                <Card className="visibility-container">
                    <Radio.Group value={ visibility } className="visibility-card">
                        <div value="public" className={ publicDisabledClasses } onClick={ publicDisabled ? () => {} : () => handleVisibility( 'public' ) }>
                            <div className="visibility-field">
                                <PaperIcon icon={ <Unlock fill={ publicDisabled ? 'rgba(0,0,0,0.65)' : '#000' } className="unlock-icon test1" /> } />
                                <div className="visibility-text">
                                    <LocaleString className="bold-header" code="recognition.visibility.public" />
                                    <div className="privacy-sub">
                                        <span className="privacy-text">
                                            <LocaleString code="recognition.visibility.publicText" />
                                        </span>
                                    </div>
                                </div>
                                <Radio disabled={ publicDisabled } value="public" />
                            </div>
                        </div>

                        <div value="private" className={ privateDisabledClasses } onClick={ privateDisabled ? () => {} : () => handleVisibility( 'private' ) }>
                            <div className="visibility-field">
                                <PaperIcon icon={ <Lock fill={ privateDisabled ? 'rgba(0,0,0,0.65)' : '#000' } className="lock-icon test1" /> } />
                                <div className="visibility-text">
                                    <LocaleString className="bold-header" code="recognition.visibility.private" />
                                    <div className="privacy-sub">
                                        <span className="privacy-text">
                                            <LocaleString code="recognition.visibility.privateText" />
                                        </span>
                                    </div>
                                </div>
                                <Radio disabled={ privateDisabled } value="private" />
                            </div>
                        </div>
                    </Radio.Group>
                </Card>
            </div>
        </CSSTransition>
    );
}
